import api from 'services/api';
import auth from 'services/auth';
import session from 'actions/session';
import partner from './partner';

export const USER_FETCH = 'user/fetch';
export const USER_LOGIN = 'user/login';
export const USER_LOGOUT = 'user/logout';
export const USER_RECOVER = 'user/recover';
export const USER_UPDATE = 'user/update';
export const USER_PASSWORD_UPDATE = 'user/updatePassword';
export const USER_FETCH_INVITED = 'user/fetchInvited';
export const USER_UPDATE_INVITED = 'user/updateInvited';
export const USER_PUT_SENDGRID_CONTACT = 'user/putSendgridContact';
export const USER_MEMBERS_FETCH = 'user/members/fetch';
export const USER_MEMBERS_INVITE = 'user/members/invite';
export const USER_MEMBERS_RESEND_INVITATION = 'user/members/resendInvitation';
export const USER_MEMBERS_REMOVE = 'user/members/remove';
export const USER_MEMBERS_UPDATE = 'user/members/update';

const actions = {
  fetch() {
    return {
      type: USER_FETCH,
      payload: api.get('/user'),
    };
  },

  login(username, password, partnerId) {
    const query = partnerId ? `?partner_id=${partnerId}` : '';

    return {
      type: USER_LOGIN,
      payload: api
        .post(`/user/login${query}`, {
          username,
          password,
          partner: true,
        })
        .then((result) => {
          if (result?.session_id) {
            auth.setLoggedInPartnerSessionId(
              result.session_id,
              result.partner_id,
            );
          }
          return result;
        }),
    };
  },

  logout() {
    return {
      type: USER_LOGOUT,
      payload: api.post('/user/logout'),
    };
  },

  recover({ email, password, resetKey }) {
    return {
      type: USER_RECOVER,
      payload: api
        .post('/user/recover', {
          email,
          password,
          reset_key: resetKey,
        })
        .catch((err) => {
          return { error: { message: err.message } };
        }),
    };
  },

  // Lands on `path` of the partner dashboard (its home by default)
  switchPartner(partnerId, path = '') {
    return () => {
      return api
        .post('/user/switch', {
          partner_id: partnerId,
        })
        .then((result) => {
          if (result?.session_id) {
            auth.setLoggedInPartnerSessionId(
              result.session_id,
              result.partner_id,
            );
            window.location.href = `/${result.partner_id}${path}`;
          }
          return result;
        });
    };
  },

  performLogin(username, password, partnerId) {
    return (dispatch) => {
      return dispatch(this.login(username, password, partnerId)).then(
        (result) => {
          if (result) {
            return result.error
              ? result
              : Promise.all([
                  dispatch(partner.fetch()),
                  dispatch(session.fetch()),
                  dispatch(this.fetch()),
                ]).then(() => {
                  return result;
                });
          }
          return null;
        },
      );
    };
  },

  performLogout() {
    return (dispatch) => {
      // Captured before the request so the cookie removed is the one this
      // session was issued under, even if the session fetch below fails
      const partnerId = auth.getSessionPartnerId();

      return dispatch(this.logout()).then(() => {
        auth.removeSessionCookie(partnerId);
        return dispatch(session.fetch());
      });
    };
  },

  update(data) {
    return {
      type: USER_UPDATE,
      payload: api.put('/user', data),
    };
  },

  updatePassword(password) {
    return {
      type: USER_PASSWORD_UPDATE,
      payload: api.put('/user/password', { password }),
    };
  },

  fetchInvited(inviteKey) {
    return {
      type: USER_FETCH_INVITED,
      payload: api.get(`/user/invited/${inviteKey}`),
    };
  },

  updateInvited(inviteKey, data) {
    return {
      type: USER_UPDATE_INVITED,
      payload: api.post(`/user/invited/${inviteKey}`, data),
    };
  },

  putSendgridContact(query) {
    return {
      type: USER_PUT_SENDGRID_CONTACT,
      payload: api.put(`/user/sendgrid`, query),
    };
  },

  // The partner's team (`/partner/users`)
  members: {
    fetch() {
      return {
        type: USER_MEMBERS_FETCH,
        payload: api.get('/partner/users'),
      };
    },

    invite(data) {
      return {
        type: USER_MEMBERS_INVITE,
        payload: api.post('/partner/users', data),
      };
    },

    // Writes the member's authorization fields (`notify_updates`)
    update(id, data) {
      return {
        type: USER_MEMBERS_UPDATE,
        payload: api.put(`/partner/users/${id}`, data),
      };
    },

    // The server generates the invite key and notification
    resendInvitation(id) {
      return {
        type: USER_MEMBERS_RESEND_INVITATION,
        payload: api.post(`/partner/users/${id}/resend-invite`),
      };
    },

    remove(id) {
      return {
        type: USER_MEMBERS_REMOVE,
        payload: api.delete(`/partner/users/${id}`),
      };
    },
  },
};

export const initialState = {
  email: null,
  name: null,
  nameInitials: null,
  firstName: null,
  lastName: null,
  invited: null,
  authorizations: [],
  partner_authorizations: [],
  loading: false,
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case USER_FETCH:
    case USER_UPDATE:
      if (!action.payload || action.payload.errors) {
        return state;
      }
      return calculateUser({
        ...state,
        ...action.payload,
      });

    case USER_LOGIN:
      if (action.payload === null) {
        return state;
      }
      if (action.payload.error) {
        return state;
      }
      return calculateUser({
        ...state,
        ...action.payload,
      });

    case USER_LOGOUT:
      return {};

    case USER_FETCH_INVITED:
      return {
        ...state,
        invited: action.payload,
      };

    default:
      return state;
  }
}

function calculateUser(user) {
  return {
    ...user,
    ...getNameFields(user),
  };
}

export function getNameFields(user) {
  const userName = String(
    user.name || user.email.replace(/\+/g, ' ').split('@').shift(),
  );
  const parts = userName.split(' ');
  return {
    name: userName,
    nameInitials: `${parts[0][0]}${parts[1] ? parts[1][0] : ''}`.toUpperCase(),
    firstName: parts.shift(),
    lastName: parts.join(' '),
  };
}

export default actions;
